import { useEffect, useRef, useState } from 'react';
import type { EvidenceStage, QiMedia } from '../lib/types';
import { formatDate } from '../lib/constants';

const EVIDENCE_STAGE_OPTIONS: { value: EvidenceStage; label: string }[] = [
  { value: 'before', label: '施炁前' },
  { value: 'during', label: '施炁當下' },
  { value: 'after', label: '施炁後' },
  { value: 'followup', label: '追蹤' },
];

export type MediaPatch = {
  evidence_stage?: EvidenceStage | null;
  description?: string | null;
  contains_face?: boolean;
  identifying_information?: string | null;
  internal_use_allowed?: boolean;
  public_use_allowed?: boolean;
  permission_withdrawn_at?: string | null;
};

interface MediaSectionProps {
  media: QiMedia[];
  mediaUrls: Record<number, string>;
  uploading: boolean;
  onUpload: (files: File[]) => void;
  onPatch: (mediaId: number, patch: MediaPatch) => void;
  onDelete?: (mediaId: number) => void;
  deletingMediaId?: number | null;
  isRecipient: boolean;
  mediaPublicConsent?: boolean;
}

// 「證據與附件」共用區：上傳＋逐檔授權。上傳不等於同意公開。
// 原始檔一律存私人空間；公開授權預設關閉，含可辨識資訊的媒體須由案主本人授權。
export default function MediaSection({
  media,
  mediaUrls,
  uploading,
  onUpload,
  onPatch,
  onDelete,
  deletingMediaId = null,
  isRecipient,
  mediaPublicConsent = false,
}: MediaSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  // 讓使用者拖到附件框或頁面任何位置都能上傳，不會被瀏覽器直接開啟檔案。
  useEffect(() => {
    const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const handleDocumentDragEnter = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (!uploading) setDragging(true);
    };
    const handleDocumentDragOver = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      if (!uploading) setDragging(true);
    };
    const handleDocumentDrop = (e: DragEvent) => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setDragging(false);
      if (uploading) return;
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length > 0) onUpload(files);
    };
    const handleDocumentDragLeave = (e: DragEvent) => {
      // relatedTarget 為 null 表示游標已真正離開瀏覽器視窗。
      if (e.relatedTarget === null) setDragging(false);
    };
    const handleDocumentDragEnd = () => setDragging(false);
    // capture=true：先於頁面內任何元件取得拖放事件，避免子元件攔截。
    document.addEventListener('dragenter', handleDocumentDragEnter, true);
    document.addEventListener('dragover', handleDocumentDragOver, true);
    document.addEventListener('drop', handleDocumentDrop, true);
    document.addEventListener('dragleave', handleDocumentDragLeave, true);
    document.addEventListener('dragend', handleDocumentDragEnd, true);
    return () => {
      document.removeEventListener('dragenter', handleDocumentDragEnter, true);
      document.removeEventListener('dragover', handleDocumentDragOver, true);
      document.removeEventListener('drop', handleDocumentDrop, true);
      document.removeEventListener('dragleave', handleDocumentDragLeave, true);
      document.removeEventListener('dragend', handleDocumentDragEnd, true);
    };
  }, [onUpload, uploading]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) onUpload(Array.from(files));
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (uploading) return;
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onUpload(files);
  };

  return (
    <>
    {dragging && (
      <div className="fixed inset-0 z-50 pointer-events-none bg-accent-500/10 border-4 border-accent-500 flex items-center justify-center">
        <div className="rounded-xl bg-background-50 px-8 py-5 shadow-lg text-accent-700 font-medium">放開即可上傳檔案</div>
      </div>
    )}
    <div
      className={`space-y-3 rounded-xl border-2 border-dashed p-4 transition-colors ${
        dragging ? 'border-accent-500 bg-accent-50/60' : 'border-foreground-200/70'
      }`}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!uploading) setDragging(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
      }}
      onDrop={handleDrop}
    >
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium text-foreground-800">證據與附件</h3>
        <label className="whitespace-nowrap inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer">
          <i className="ri-upload-line" />
          {uploading ? '上傳中…' : '上傳照片／影片／文件'}
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="image/*,video/*,audio/*,.aac,.acc,.adts,.m4a,.mp3,.wav,.ogg,.oga,.flac,.opus,.pdf,.doc,.docx,.txt"
            onChange={handleChange}
            disabled={uploading}
            className="hidden"
          />
        </label>
      </div>

      <p className="text-xs text-foreground-400">
        原始檔一律存於私人空間。上傳不等於同意公開，每檔需分別選擇是否公開；含臉部或可辨識資訊的媒體須由案主本人授權公開。
      </p>
      <p className={`text-xs rounded-md px-3 py-2 ${mediaPublicConsent ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
        {mediaPublicConsent
          ? '✓ 受施者已同意公開「指定」照片或影片；仍須在下方逐檔勾選要公開的檔案。'
          : '受施者尚未同意公開照片或影片；所有附件維持私人保存。'}
      </p>
      <p className={`text-xs text-center py-2 ${dragging ? 'text-accent-700 font-medium' : 'text-foreground-400'}`}>
        {dragging ? '放開即可上傳' : '也可以把照片、影片或文件直接拖放到這個框內'}
      </p>

      {media.length === 0 ? (
        <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-4">尚無附件。</p>
      ) : (
        <div className="space-y-3">
          {media.map((m) => (
            <MediaCard
              key={m.id}
              media={m}
              url={mediaUrls[m.id]}
              isRecipient={isRecipient}
              mediaPublicConsent={mediaPublicConsent}
              onPatch={(patch) => onPatch(m.id, patch)}
              onDelete={onDelete && m.file_path.includes('/recipient/') ? () => onDelete(m.id) : undefined}
              deleting={deletingMediaId === m.id}
            />
          ))}
        </div>
      )}
    </div>
    </>
  );
}

function MediaCard({
  media,
  url,
  isRecipient,
  mediaPublicConsent,
  onPatch,
  onDelete,
  deleting,
}: {
  media: QiMedia;
  url?: string;
  isRecipient: boolean;
  mediaPublicConsent: boolean;
  onPatch: (patch: MediaPatch) => void;
  onDelete?: () => void;
  deleting: boolean;
}) {
  const isFacePhotoOrVideo = media.contains_face && (media.file_type === 'image' || media.file_type === 'video');
  const publicBlocked = isFacePhotoOrVideo && !isRecipient;
  // 相容修正前已被存成 document 的 AAC/音訊附件。
  const isAudio = media.file_type === 'audio'
    || /\.(aac|acc|adts|m4a|mp3|wav|ogg|oga|flac|opus)$/i.test(media.file_path);

  return (
    <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-4">
      <div className="bg-background-50 border border-foreground-200/50 rounded-lg overflow-hidden min-h-36">
        {url ? (
          media.file_type === 'image' ? (
            <img src={url} alt="施炁紀錄檔案" className="w-full h-36 object-contain" />
          ) : media.file_type === 'video' ? (
            <div className="space-y-1 p-2">
              <video src={url} controls preload="metadata" playsInline className="w-full h-28 bg-black rounded object-contain" />
              <a href={url} target="_blank" rel="noreferrer" className="block text-center text-[11px] text-accent-700 hover:underline">若無法播放，開啟原始檔</a>
            </div>
          ) : isAudio ? (
            <div className="w-full h-36 flex items-center px-3">
              <audio src={url} controls preload="metadata" className="w-full" />
            </div>
          ) : (
            <div className="w-full h-36 flex items-center justify-center">
              <i className="ri-file-text-line text-2xl text-foreground-400" />
            </div>
          )
        ) : (
          <div className="w-full h-36 flex items-center justify-center">
            <i className="ri-image-line text-2xl text-foreground-300" />
          </div>
        )}
      </div>

      <div className="space-y-2.5">
        <p className={`text-[11px] rounded px-2.5 py-1.5 ${media.public_use_allowed ? 'bg-emerald-50 text-emerald-700' : mediaPublicConsent ? 'bg-amber-50 text-amber-700' : 'bg-background-200 text-foreground-500'}`}>
          {media.public_use_allowed
            ? '✓ 此檔已被指定同意公開'
            : mediaPublicConsent
              ? '受施者已同意逐檔指定；此檔尚未勾選公開'
              : '此檔未獲公開同意，僅私人保存'}
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={media.evidence_stage || ''}
            onChange={(e) => onPatch({ evidence_stage: (e.target.value || null) as EvidenceStage | null })}
            className="px-2 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700"
          >
            <option value="">選擇階段</option>
            {EVIDENCE_STAGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <span className="text-[10px] text-foreground-400">{media.file_type}</span>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting}
              className="ml-auto text-xs text-rose-600 hover:text-rose-700 cursor-pointer disabled:opacity-50"
            >
              {deleting ? '刪除中…' : '刪除附件'}
            </button>
          )}
        </div>

        <input
          type="text"
          value={media.description || ''}
          onChange={(e) => onPatch({ description: e.target.value || null })}
          placeholder="說明（可留白）"
          className="w-full px-2 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700"
        />

        <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
          <input
            type="checkbox"
            checked={media.contains_face}
            onChange={(e) => onPatch({ contains_face: e.target.checked })}
            className="w-4 h-4 accent-accent-500"
          />
          含可辨識臉部或人物
        </label>

        <input
          type="text"
          value={media.identifying_information || ''}
          onChange={(e) => onPatch({ identifying_information: e.target.value || null })}
          placeholder="含姓名、地址、證件或第三人資料（可留白）"
          className="w-full px-2 py-1.5 rounded-md border border-foreground-200 bg-background-50 text-xs text-foreground-700"
        />

        <div className="flex items-center gap-4 flex-wrap">
          <label className="flex items-center gap-2 text-xs text-foreground-600 cursor-pointer">
            <input
              type="checkbox"
              checked={media.internal_use_allowed}
              onChange={(e) => onPatch({ internal_use_allowed: e.target.checked })}
              className="w-4 h-4 accent-accent-500"
            />
            允許內部審核
          </label>
          <label className={`flex items-center gap-2 text-xs cursor-pointer ${publicBlocked ? 'text-foreground-400' : 'text-foreground-600'}`}>
            <input
              type="checkbox"
              checked={media.public_use_allowed}
              disabled={publicBlocked}
              onChange={(e) => {
                if (e.target.checked) {
                  onPatch({ public_use_allowed: true, permission_withdrawn_at: null });
                } else {
                  onPatch({ public_use_allowed: false });
                }
              }}
              className="w-4 h-4 accent-accent-500"
            />
            允許匿名公開
          </label>
          {media.public_use_allowed && (
            <button
              type="button"
              onClick={() => onPatch({ public_use_allowed: false, permission_withdrawn_at: new Date().toISOString() })}
              className="whitespace-nowrap text-xs text-rose-600 hover:text-rose-700 cursor-pointer"
            >
              撤回公開授權
            </button>
          )}
        </div>

        {publicBlocked && (
          <p className="text-[10px] text-amber-600">此照片／影片含可辨識臉部，僅案主本人可逐檔授權公開（施炁者不能代替同意）。</p>
        )}
        {media.permission_withdrawn_at && (
          <p className="text-[10px] text-rose-500">此檔公開授權已於 {formatDate(media.permission_withdrawn_at)} 撤回。</p>
        )}
      </div>
    </div>
  );
}
